import { canAccessFeature, TierFeatureKey } from '../constants/planFeatures';
import type { Club } from '../types/organization';
import type { ClubPlanTier, SubscriptionTier } from '../constants/planFeatures';
import { academyPublicOrigin, type AcademyPublicUrlOptions } from './academyPublicUrl';

/** API error when path-mode club slug lookup is blocked by an active branded subdomain. */
export const PUBLIC_CLUB_BRANDED_SUBDOMAIN_CODE = 'BRANDED_SUBDOMAIN_ACTIVE' as const;

export type ClubPublicUrlEnv = 'production' | 'staging' | 'development';

type ClubHostMode = 'path' | 'subdomain';

export interface ClubPublicUrlOptions extends AcademyPublicUrlOptions {}

function normalizeOrigin(origin: string): string {
  return origin.replace(/\/$/, '');
}

function inferEnv(): ClubPublicUrlEnv {
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') {
    const web = process.env.WEB_APP_URL ?? '';
    if (web.includes('dev.') || web.includes('-dev')) return 'staging';
    return 'production';
  }
  if (typeof globalThis !== 'undefined') {
    const loc = (globalThis as { location?: { hostname?: string; port?: string } }).location;
    const host = loc?.hostname;
    if (host) {
      if (host === 'localhost' || host.endsWith('.localhost')) return 'development';
      if (host === 'dev.courtsync.io' || host.endsWith('.dev.courtsync.io')) return 'staging';
      return 'production';
    }
  }
  return 'development';
}

function defaultWebAppOrigin(env: ClubPublicUrlEnv): string {
  switch (env) {
    case 'production':
      return 'https://app.courtsync.io';
    case 'staging':
      return 'https://app.dev.courtsync.io';
    default:
      return 'http://localhost:3000';
  }
}

function defaultSubdomainBaseDomain(env: ClubPublicUrlEnv): string {
  switch (env) {
    case 'production':
      return 'courtsync.io';
    case 'staging':
      return 'dev.courtsync.io';
    default:
      return 'localhost';
  }
}

export function clubSubdomainHost(label: string, options: ClubPublicUrlOptions = {}): string {
  const env = options.env ?? inferEnv();
  const baseDomain = options.subdomainBaseDomain ?? defaultSubdomainBaseDomain(env);
  const normalized = label.trim().toLowerCase();
  if (env === 'development' && baseDomain === 'localhost') {
    const port = (globalThis as { location?: { port?: string } }).location?.port;
    const portSuffix = port ? `:${port}` : ':3000';
    return `${normalized}.localhost${portSuffix}`;
  }
  return `${normalized}.${baseDomain}`;
}

/**
 * Whether the club's branded subdomain is active. Pass org `clubTier` (not academy tier).
 */
export function isBrandedClubSubdomainActive(
  club: Pick<Club, 'subdomain' | 'subdomainEnabled'>,
  clubTier: ClubPlanTier | SubscriptionTier
): boolean {
  if (!club.subdomain?.trim() || club.subdomainEnabled !== true) {
    return false;
  }
  return canAccessFeature(clubTier, TierFeatureKey.BRANDED_SUBDOMAIN);
}

export function clubPublicOrigin(
  club: Pick<Club, 'slug' | 'subdomain' | 'subdomainEnabled'>,
  clubTier: ClubPlanTier | SubscriptionTier,
  options: ClubPublicUrlOptions = {}
): string {
  const env = options.env ?? inferEnv();
  const webAppOrigin = normalizeOrigin(options.webAppOrigin ?? defaultWebAppOrigin(env));
  const baseDomain = options.subdomainBaseDomain ?? defaultSubdomainBaseDomain(env);

  if (isBrandedClubSubdomainActive(club, clubTier)) {
    const label = club.subdomain!.trim().toLowerCase();
    if (env === 'development' && baseDomain === 'localhost') {
      const port = (globalThis as { location?: { port?: string } }).location?.port;
      const portSuffix = port ? `:${port}` : ':3000';
      return `http://${label}.localhost${portSuffix}`;
    }
    return `https://${label}.${baseDomain}`;
  }

  return webAppOrigin;
}

export function clubPublicPathPrefix(
  club: Pick<Club, 'slug' | 'subdomain' | 'subdomainEnabled'>,
  clubTier: ClubPlanTier | SubscriptionTier,
  hostMode: ClubHostMode
): string {
  if (hostMode === 'subdomain' && isBrandedClubSubdomainActive(club, clubTier)) {
    return '';
  }
  return `/club/${club.slug}`;
}

export function clubPublicPath(
  club: Pick<Club, 'slug' | 'subdomain' | 'subdomainEnabled'>,
  clubTier: ClubPlanTier | SubscriptionTier,
  subpath: string,
  hostMode: ClubHostMode = 'path'
): string {
  const prefix = clubPublicPathPrefix(club, clubTier, hostMode);
  const normalized = subpath.startsWith('/') ? subpath : `/${subpath}`;
  if (!prefix) return normalized;
  return `${prefix}${normalized}`;
}

export function clubPublicBaseUrl(
  club: Pick<Club, 'slug' | 'subdomain' | 'subdomainEnabled'>,
  clubTier: ClubPlanTier | SubscriptionTier,
  subpath = '',
  options: ClubPublicUrlOptions = {},
  hostMode: ClubHostMode = 'path'
): string {
  const origin = clubPublicOrigin(club, clubTier, options);
  const effectiveHostMode: ClubHostMode =
    isBrandedClubSubdomainActive(club, clubTier) ? 'subdomain' : hostMode;
  const path = subpath
    ? clubPublicPath(club, clubTier, subpath, effectiveHostMode)
    : clubPublicPathPrefix(club, clubTier, effectiveHostMode);
  return path ? `${origin}${path}` : origin;
}

/** Re-export academy origin helper for combined org landing that uses academy subdomain. */
export { academyPublicOrigin };
